import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import { Button } from '@/components/ui/Button';
import { supabase } from '@/lib/supabase';

/**
 * Completes the Supabase OAuth (PKCE) sign-in flow.
 *
 * Google -> Supabase redirects the user back here with `?code=...`. We exchange
 * the code for a session explicitly so failures (missing code_verifier, expired
 * code, etc.) become visible errors instead of a silent spinner.
 *
 * The route is also reached when the user signs in with a magic link (hash-
 * based tokens), which Supabase still parses on its own when we call
 * `getSession()` after stripping the URL.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const ranRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Strict-mode guard: in dev React mounts the effect twice. The OAuth code
    // is single-use, so we must only attempt the exchange once.
    if (ranRef.current) return;
    ranRef.current = true;

    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const errorDescription =
      url.searchParams.get('error_description') ??
      new URLSearchParams(url.hash.replace(/^#/, '')).get('error_description');

    console.log('[Callback] mounted', {
      pathname: url.pathname,
      hasCode: !!code,
      hasError: !!errorDescription,
    });

    if (errorDescription) {
      const msg = decodeURIComponent(errorDescription.replace(/\+/g, ' '));
      setError(msg);
      toast.error(msg);
      return;
    }

    void (async () => {
      // If we already have a valid session (e.g. the user refreshed this page
      // after a previous successful exchange), skip straight to the app.
      const { data: existing } = await supabase.auth.getSession();
      console.log('[Callback] getSession before exchange', {
        hasSession: !!existing.session,
      });
      if (existing.session) {
        window.history.replaceState({}, '', '/auth/callback');
        console.log('[Callback] existing session found, navigating to /dashboard');
        navigate('/dashboard', { replace: true });
        return;
      }

      if (!code) {
        console.log('[Callback] no code in URL, sending to sign-in');
        navigate('/auth/sign-in', { replace: true });
        return;
      }

      console.log('[Callback] exchanging code for session...');
      const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
      console.log('[Callback] exchange result', {
        hasSession: !!data?.session,
        error: exchangeError?.message,
      });

      // Clean the code out of the URL regardless of outcome so a refresh
      // doesn't try to reuse it.
      window.history.replaceState({}, '', '/auth/callback');

      if (exchangeError) {
        console.error('[Callback] exchangeCodeForSession failed', exchangeError);
        const msg = humanizeAuthError(exchangeError.message);
        setError(msg);
        toast.error(msg);
        return;
      }

      if (data.session) {
        console.log('[Callback] navigating to /dashboard');
        navigate('/dashboard', { replace: true });
      } else {
        const msg = 'Signed in, but no session returned. Please try again.';
        setError(msg);
        toast.error(msg);
      }
    })();
  }, [navigate]);

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-background p-6">
        <div className="max-w-md space-y-4 text-center">
          <h1 className="text-xl font-medium tracking-headline">Sign-in failed</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button onClick={() => navigate('/auth/sign-in', { replace: true })}>
            Back to sign-in
          </Button>
        </div>
      </div>
    );
  }

  return <LoadingScreen />;
}

function humanizeAuthError(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower.includes('code verifier')) {
    return (
      'Sign-in could not be completed because the browser lost the OAuth handshake. ' +
      'This usually means you started sign-in on a different URL than this one ' +
      '(e.g. with vs. without "www."). Make sure you click "Continue with Google" ' +
      'from this same address.'
    );
  }
  if (lower.includes('code already used') || lower.includes('invalid_grant')) {
    return 'This sign-in link has already been used. Please start sign-in again.';
  }
  return raw;
}
